import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Inbox, ChevronRight } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { StatusBadge, PriorityBadge } from "@/components/help/SupportBadges";
import { api } from "@/lib/api";

const sel = "rounded-md border border-line bg-bg px-2.5 py-1.5 text-xs text-ink-primary focus:outline-none focus:border-brand-amber/60";

export default function SupportTickets() {
  const [tickets, setTickets] = useState(null);
  const [meta, setMeta] = useState(null);
  const [f, setF] = useState({ status: "", priority: "", category: "", station_id: "", since: "" });

  const load = (filters = f) => {
    const qs = Object.entries(filters).filter(([, v]) => v).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
    api.get(`/helpdesk/tickets${qs ? `?${qs}` : ""}`).then((r) => setTickets(r.data.tickets)).catch(() => setTickets([]));
  };
  useEffect(() => {
    api.get("/helpdesk/meta").then((r) => setMeta(r.data)).catch(() => {});
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const setFilter = (k) => (e) => {
    const next = { ...f, [k]: e.target.value };
    setF(next); load(next);
  };

  return (
    <div className="space-y-6" data-testid="my-tickets-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PageHeader eyebrow="// SUPPORT" title="My Support Tickets"
          description="Track your conversations with the BroadcastFlow support team." />
        <Link to="/dashboard/support/new" data-testid="new-ticket-btn"
          className="inline-flex items-center gap-1.5 rounded-md bg-brand-amber px-4 py-2.5 text-sm font-semibold text-black hover:bg-brand-amber/90 transition">
          <Plus className="h-4 w-4" /> Submit a ticket
        </Link>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="ticket-filters">
        <select className={sel} value={f.status} onChange={setFilter("status")} data-testid="filter-status">
          <option value="">All statuses</option>
          {(meta?.statuses || []).map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
        </select>
        <select className={sel} value={f.priority} onChange={setFilter("priority")} data-testid="filter-priority">
          <option value="">All priorities</option>
          {(meta?.priorities || []).map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <select className={sel} value={f.category} onChange={setFilter("category")} data-testid="filter-category">
          <option value="">All categories</option>
          {(meta?.categories || []).map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
        {(meta?.stations || []).length > 1 && (
          <select className={sel} value={f.station_id} onChange={setFilter("station_id")} data-testid="filter-station">
            <option value="">All stations</option>
            {meta.stations.map((s) => <option key={s.station_id} value={s.station_id}>{s.name}</option>)}
          </select>
        )}
        <input type="date" className={sel} value={f.since ? f.since.slice(0, 10) : ""} data-testid="filter-date"
          onChange={(e) => { const next = { ...f, since: e.target.value ? new Date(e.target.value).toISOString() : "" }; setF(next); load(next); }} />
      </div>

      {tickets === null ? (
        <div className="font-mono text-xs text-brand-amber">LOADING…</div>
      ) : tickets.length === 0 ? (
        <div className="rounded-xl border border-line bg-bg-surface p-10 text-center" data-testid="tickets-empty">
          <Inbox className="h-8 w-8 text-ink-muted mx-auto mb-3" />
          <p className="text-sm text-ink-secondary mb-1">No tickets here yet.</p>
          <p className="text-xs text-ink-muted">Need a hand? Search the <Link to="/dashboard/help" className="text-brand-amber hover:underline">Help Center</Link> or submit a ticket.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {tickets.map((t) => (
            <Link key={t.reference} to={`/dashboard/support/tickets/${t.reference}`}
              data-testid={`ticket-row-${t.reference}`}
              className="flex items-center gap-4 rounded-xl border border-line bg-bg-surface px-4 py-3.5 hover:border-brand-amber/40 hover:bg-bg-elevated transition group">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <span className="font-mono text-[10px] text-brand-amber">{t.reference}</span>
                  <StatusBadge status={t.status} />
                  <PriorityBadge priority={t.priority} />
                </div>
                <div className="text-sm text-ink-primary truncate group-hover:text-brand-amber transition">{t.subject}</div>
                <div className="flex flex-wrap items-center gap-3 mt-1 font-mono text-[9px] uppercase tracking-wider text-ink-muted">
                  <span>{t.category_label}</span>
                  {t.station_name && <span>{t.station_name}</span>}
                  {t.assignee_name && <span>Agent: {t.assignee_name}</span>}
                  <span>Created {new Date(t.created_at).toLocaleDateString()}</span>
                  <span>Last reply: {t.last_response_by === "agent" ? "support" : "you"} · {new Date(t.last_response_at).toLocaleString()}</span>
                </div>
              </div>
              <ChevronRight className="h-4 w-4 text-ink-muted shrink-0" />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
